import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Briefcase, FilePlus2, Search, Sparkles } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Badge } from '@/components/ui/Badge'
import { Skeleton } from '@/components/ui/Skeleton'
import { useDebounce } from '@/hooks/useDebounce'
import { cn } from '@/lib/cn'
import { useQuery } from '@tanstack/react-query'
import type { AlertSummary, Priority } from '@/features/alerts/types'
import { listCases } from '../api'
import { useLinkToCase } from '../hooks/useCaseGrouping'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  alertIds: readonly string[]
  /** Initial mode — "Link to case" → 'pick'; "Create case" → 'create'. */
  defaultMode?: 'pick' | 'create'
  /** Called after the link succeeds. */
  onLinked: (info: { caseId: string; created: boolean }) => void
  /**
   * Full alert summaries for the selection. When provided, the
   * Create panel derives title / summary / priority / queue
   * defaults from these. Callers that only have ids (queue bulk
   * actions) can omit it; the form renders empty defaults and the
   * analyst types from scratch.
   */
  prefillAlerts?: ReadonlyArray<AlertSummary>
}

type Step = 'compose' | 'confirm'

const CONFIRM_THRESHOLD = 3

/**
 * AF-127 — Alert linker dialog.
 *
 * Two modes (existing / create) plus a confirmation step that gates any
 * link involving 3+ alerts (per AC: "Confirmation modal before creating
 * a case with 3+ alerts (prevent accidental mass-grouping)").
 *
 * The 10-second Sonner Undo toast is owned by `useLinkToCase` so every
 * caller of the hook gets the same affordance — keyboard, picker, suggestion
 * panel, bulk bar.
 */
export function AlertLinker({
  open,
  onOpenChange,
  alertIds,
  defaultMode = 'pick',
  onLinked,
  prefillAlerts,
}: Props) {
  // Derive defaults from the selected alerts so the Create panel
  // opens with a sensible, consistent starting point — same
  // scenario / customer / priority as the source alerts.
  // The bundle size for prefill purposes is alertIds.length, not
  // prefillAlerts.length. Suggested-grouping callers pass a single
  // anchoring alert as prefill data even when the bundle is larger;
  // derivePrefill uses bundleSize for the "(N alerts)" suffix so
  // the title reflects the actual case being created.
  const prefill = useMemo(
    () => derivePrefill(prefillAlerts ?? [], alertIds.length),
    [prefillAlerts, alertIds.length],
  )
  const [step, setStep] = useState<Step>('compose')
  const [mode, setMode] = useState<'pick' | 'create'>(defaultMode)
  const [search, setSearch] = useState('')
  const [newTitle, setNewTitle] = useState(prefill.title)
  const [newSummary, setNewSummary] = useState(prefill.summary)
  const [newPriority, setNewPriority] = useState<Priority>(prefill.priority)
  // queue is derived but not user-editable — the analyst sees what
  // they're inheriting; the case-detail page lets them reassign
  // later if needed.
  const [pickedCaseId, setPickedCaseId] = useState<string | null>(null)
  const debounced = useDebounce(search, 250)

  // Sync the prefill into local state whenever the source alerts
  // change — the dialog is reused across openings, so the second
  // time it opens for a different alert it should reflect that
  // alert's defaults.
  useEffect(() => {
    if (!open) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing local form/draft state from a fetched/prop value; the deps array is narrow enough that the cascade is bounded to the data-change case
    setNewTitle(prefill.title)
     
    setNewSummary(prefill.summary)
     
    setNewPriority(prefill.priority)
  }, [open, prefill.title, prefill.summary, prefill.priority])

  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const newTitleInputRef = useRef<HTMLInputElement | null>(null)

  // Reset internal state on close — done via the open-change handler below
  // (not in a useEffect) so the lint `set-state-in-effect` rule is satisfied.
  const handleOpenChange = (next: boolean): void => {
    if (!next) {
      setStep('compose')
      setSearch('')
      setNewTitle('')
      setPickedCaseId(null)
    }
    onOpenChange(next)
  }

  // Focus the active input on open / mode flip.
  useEffect(() => {
    if (!open || step !== 'compose') return
    const id = window.setTimeout(() => {
      if (mode === 'pick') searchInputRef.current?.focus()
      else newTitleInputRef.current?.focus()
    }, 0)
    return () => window.clearTimeout(id)
  }, [open, mode, step])

  const cases = useQuery({
    queryKey: ['cases', 'search', debounced],
    queryFn: () => listCases({ q: debounced || undefined }),
    enabled: mode === 'pick' && step === 'compose',
    staleTime: 10_000,
    select: (data) => data.cases,
  })

  const link = useLinkToCase()

  const fire = (): void => {
    if (mode === 'pick' && pickedCaseId) {
      link.mutate(
        { alert_ids: [...alertIds], case_id: pickedCaseId },
        {
          onSuccess: (data) => {
            onLinked({ caseId: data.case_id, created: data.created })
            handleOpenChange(false)
          },
        },
      )
    } else if (mode === 'create' && newTitle.trim().length >= 3) {
      link.mutate(
        {
          alert_ids: [...alertIds],
          case_id: null,
          new_case_title: newTitle.trim(),
          new_case_summary: newSummary.trim() || undefined,
          new_case_priority: newPriority,
          new_case_queue: prefill.queue || undefined,
        },
        {
          onSuccess: (data) => {
            onLinked({ caseId: data.case_id, created: data.created })
            handleOpenChange(false)
          },
        },
      )
    }
  }

  const submit = (): void => {
    if (alertIds.length >= CONFIRM_THRESHOLD) {
      setStep('confirm')
      return
    }
    fire()
  }

  const composeIsValid =
    (mode === 'pick' && pickedCaseId !== null) ||
    (mode === 'create' && newTitle.trim().length >= 3)

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        {step === 'compose' ? (
          <>
            <DialogHeader>
              <DialogTitle>
                Add {alertIds.length} alert{alertIds.length === 1 ? '' : 's'} to a case
              </DialogTitle>
              <DialogDescription>
                Group these alerts under a single investigation. Case actions and notes apply
                to every linked alert.
              </DialogDescription>
            </DialogHeader>

            <div className="flex gap-1 border-b border-border">
              <ModeTab active={mode === 'pick'} onClick={() => setMode('pick')}>
                <Briefcase size={12} /> Existing case
              </ModeTab>
              <ModeTab active={mode === 'create'} onClick={() => setMode('create')}>
                <FilePlus2 size={12} /> Create new
              </ModeTab>
            </div>

            {mode === 'pick' ? (
              <PickPanel
                searchInputRef={searchInputRef}
                search={search}
                onSearchChange={setSearch}
                cases={cases.data ?? []}
                isLoading={cases.isLoading}
                pickedCaseId={pickedCaseId}
                onPick={setPickedCaseId}
              />
            ) : (
              <CreatePanel
                newTitleInputRef={newTitleInputRef}
                newTitle={newTitle}
                onNewTitleChange={setNewTitle}
                newSummary={newSummary}
                onNewSummaryChange={setNewSummary}
                newPriority={newPriority}
                onNewPriorityChange={setNewPriority}
                queue={prefill.queue}
                scenarios={prefill.scenarios}
                customers={prefill.customers}
                alertCount={alertIds.length}
              />
            )}

            <DialogFooter>
              <Button variant="ghost" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button disabled={!composeIsValid || link.isPending} onClick={submit}>
                {link.isPending
                  ? 'Linking…'
                  : mode === 'pick'
                    ? 'Add to case'
                    : 'Create case'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <ConfirmStep
            alertIds={alertIds}
            mode={mode}
            pickedCaseTitle={
              cases.data?.find((c) => c.id === pickedCaseId)?.title ?? null
            }
            newTitle={newTitle}
            pending={link.isPending}
            onBack={() => setStep('compose')}
            onConfirm={fire}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

function PickPanel({
  searchInputRef,
  search,
  onSearchChange,
  cases,
  isLoading,
  pickedCaseId,
  onPick,
}: {
  searchInputRef: React.RefObject<HTMLInputElement | null>
  search: string
  onSearchChange: (next: string) => void
  cases: ReadonlyArray<{
    id: string
    case_number: string
    title: string
    status: string
    alert_count: number
  }>
  isLoading: boolean
  pickedCaseId: string | null
  onPick: (id: string) => void
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <Search
          size={12}
          className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-fg"
          aria-hidden
        />
        <Input
          ref={searchInputRef}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by case number or title…"
          aria-label="Search cases"
          className="h-8 pl-7 text-sm"
        />
      </div>
      <div className="max-h-64 overflow-y-auto">
        {isLoading ? (
          <div className="flex flex-col gap-1">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : cases.length === 0 ? (
          <p className="px-2 py-3 text-sm text-muted-fg">No matching cases.</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {cases.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => onPick(c.id)}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 rounded border px-2 py-2 text-left text-sm transition-colors',
                    pickedCaseId === c.id
                      ? 'border-primary bg-primary/5'
                      : 'border-transparent hover:border-border hover:bg-muted',
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{c.title}</p>
                    <p className="truncate text-xs text-muted-fg">
                      <span className="font-mono">{c.case_number}</span> ·{' '}
                      {c.alert_count} alert{c.alert_count === 1 ? '' : 's'}
                    </p>
                  </div>
                  <Badge variant="default" className="capitalize">
                    {c.status.replace('_', ' ')}
                  </Badge>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function CreatePanel({
  newTitleInputRef,
  newTitle,
  onNewTitleChange,
  newSummary,
  onNewSummaryChange,
  newPriority,
  onNewPriorityChange,
  queue,
  scenarios,
  customers,
  alertCount,
}: {
  newTitleInputRef: React.RefObject<HTMLInputElement | null>
  newTitle: string
  onNewTitleChange: (next: string) => void
  newSummary: string
  onNewSummaryChange: (next: string) => void
  newPriority: Priority
  onNewPriorityChange: (next: Priority) => void
  queue: string
  scenarios: string[]
  customers: string[]
  alertCount: number
}) {
  const PRIORITIES: ReadonlyArray<{ value: Priority; label: string }> = [
    { value: 'critical', label: 'Critical' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
  ]
  const hasPrefill =
    scenarios.length > 0 || customers.length > 0 || queue !== ''
  return (
    <div className="flex flex-col gap-3">
      {hasPrefill && (
        <div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/[0.04] px-3 py-2 text-xs">
          <Sparkles size={12} className="mt-0.5 shrink-0 text-primary" aria-hidden />
          <div className="flex-1 text-fg">
            <p className="font-medium">Prefilled from {alertCount} alert{alertCount === 1 ? '' : 's'}</p>
            <p className="text-muted-fg">
              Title and summary are seeded from the selected alerts' scenarios
              and customers — edit anything before creating.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-1">
        <Label htmlFor="new-case-title">Case title</Label>
        <Input
          ref={newTitleInputRef}
          id="new-case-title"
          value={newTitle}
          onChange={(e) => onNewTitleChange(e.target.value)}
          placeholder="e.g. NIP mule cluster · OPay"
          maxLength={120}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="new-case-summary">Summary</Label>
        <textarea
          id="new-case-summary"
          value={newSummary}
          onChange={(e) => onNewSummaryChange(e.target.value)}
          placeholder="Short narrative describing what the analyst will investigate."
          rows={3}
          maxLength={1000}
          className="rounded border border-input bg-card p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <p className="text-[0.65rem] text-muted-fg">{newSummary.length}/1000</p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor="new-case-priority">Priority</Label>
          <select
            id="new-case-priority"
            value={newPriority}
            onChange={(e) => onNewPriorityChange(e.target.value as Priority)}
            className="h-9 rounded border border-input bg-card px-2 text-sm"
          >
            {PRIORITIES.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <Label>Queue</Label>
          <div className="flex h-9 items-center rounded border border-dashed border-border bg-muted/30 px-2 text-xs text-muted-fg">
            {queue || (
              <span className="italic">Inherited from alerts at create time.</span>
            )}
          </div>
        </div>
      </div>

      <p className="text-xs text-muted-fg">
        A new case will be opened with these {alertCount} alert
        {alertCount === 1 ? '' : 's'} linked.
      </p>
    </div>
  )
}

// ---- prefill derivation ----

interface PrefillState {
  title: string
  summary: string
  priority: Priority
  queue: string
  scenarios: string[]
  customers: string[]
}

const EMPTY_PREFILL: PrefillState = {
  title: '',
  summary: '',
  priority: 'medium',
  queue: '',
  scenarios: [],
  customers: [],
}

/**
 * Derive sensible defaults for the case create form from the
 * selected alerts. The goals:
 *
 *   - Title: encode the dominant scenario + customer so the case
 *     list reads "FRD-005 · Ada Eze (3 alerts)" rather than 200
 *     differently-worded variants of "fraud".
 *   - Summary: a starter narrative listing what the analyst is
 *     looking at — scenario names, customers, channels, total
 *     amount — so the case carries context without manual retyping.
 *   - Priority: the highest priority among the alerts — a critical
 *     alert in the bundle shouldn't get demoted to medium by
 *     averaging.
 *   - Queue: the most-common queue across the alerts. Backend
 *     defaults if not set; surfacing it here is the right
 *     "inherit from source" affordance.
 */
function derivePrefill(
  alerts: ReadonlyArray<AlertSummary>,
  bundleSize?: number,
): PrefillState {
  if (alerts.length === 0) return EMPTY_PREFILL
  // bundleSize defaults to the prefill array length — callers
  // grouping more alerts than they have summaries for (e.g.
  // suggestions-panel, where only the anchoring alert has full
  // detail loaded) override this so the title's "(N alerts)" suffix
  // reflects the real case being created.
  const total = bundleSize && bundleSize > 0 ? bundleSize : alerts.length

  // Most-common scenarios + customers (display name).
  const scenarioCounts = countBy(alerts, (a) => `${a.scenario_code}|${a.scenario_name}`)
  const customerCounts = countBy(alerts, (a) => a.customer.display_name)
  const queueCounts = countBy(alerts, (a) => a.queue_name || '')

  const topScenarios = sortedKeys(scenarioCounts).slice(0, 3)
  const topCustomers = sortedKeys(customerCounts).slice(0, 3)
  const queue = sortedKeys(queueCounts)[0] ?? ''

  const dominantScenario = topScenarios[0] ?? ''
  const [scenarioCode] = dominantScenario.split('|')
  const dominantCustomer = topCustomers[0] ?? ''

  // Title pattern:
  //   single alert     → "FRD-005 · Ada Eze"
  //   multi, 1 cust    → "FRD-005 · Ada Eze (3 alerts)"
  //   multi, multi     → "FRD-005 · 3 customers (3 alerts)"
  //   mixed scenarios  → "Mixed · 5 alerts on FRD-001, FRD-005"
  let title: string
  if (total === 1) {
    title = `${scenarioCode ?? ''} · ${dominantCustomer}`.replace(/^ · /, '').replace(/ · $/, '')
  } else if (topScenarios.length === 1) {
    title =
      topCustomers.length === 1
        ? `${scenarioCode} · ${dominantCustomer} (${total} alerts)`
        : `${scenarioCode} · ${topCustomers.length}+ customers (${total} alerts)`
  } else {
    title = `Mixed · ${total} alerts on ${topScenarios
      .map((s) => s.split('|')[0])
      .join(', ')}`
  }

  // Summary pattern:
  //   "1 alert on <scenario> for <customer> — <channel>. Total ₦X."
  //   "3 alerts across FRD-005 / FRD-001 on Ada Eze, Tunde A. — NIP / POS. Total ₦4.2M."
  const channels = Array.from(new Set(alerts.map((a) => a.channel).filter(Boolean)))
  const totalMinor = alerts.reduce((acc, a) => acc + (a.amount_minor ?? 0), 0)
  const scenarioBlurb = topScenarios
    .map((s) => s.split('|')[0])
    .join(', ')
  const customerBlurb = topCustomers.slice(0, 2).join(', ')
  const channelBlurb = channels.join(' / ').toUpperCase()
  const amountBlurb = totalMinor > 0 ? ` Total ${formatAmountMinor(totalMinor)}.` : ''
  // When we know the bundle is larger than the data we have,
  // hedge the summary with "+ N more" so analysts know the auto-
  // generated narrative is sampled.
  const undocumented = total - alerts.length
  const hedge = undocumented > 0 ? ` Plus ${undocumented} more alert${undocumented === 1 ? '' : 's'} in the bundle.` : ''
  const summary =
    total === 1
      ? `1 alert on ${scenarioBlurb} for ${customerBlurb} — ${channelBlurb}.${amountBlurb}`
      : `${total} alerts across ${scenarioBlurb} on ${customerBlurb}${
          topCustomers.length > 2 ? ` (+${topCustomers.length - 2} more)` : ''
        } — ${channelBlurb}.${amountBlurb}${hedge}`

  // Priority: the highest among the alerts. Order matters here —
  // a single critical drives the case to critical even if most are
  // medium. The case detail page lets the analyst downgrade later.
  const ORDER: Priority[] = ['low', 'medium', 'high', 'critical']
  let priority: Priority = 'medium'
  for (const a of alerts) {
    if (ORDER.indexOf(a.priority) > ORDER.indexOf(priority)) {
      priority = a.priority
    }
  }

  return {
    title,
    summary: summary.trim(),
    priority,
    queue,
    scenarios: topScenarios.map((s) => {
      const [code, name] = s.split('|')
      return name ? `${code} · ${name}` : code ?? ''
    }),
    customers: topCustomers,
  }
}

function countBy<T>(rows: ReadonlyArray<T>, key: (t: T) => string): Map<string, number> {
  const m = new Map<string, number>()
  for (const r of rows) {
    const k = key(r)
    if (!k) continue
    m.set(k, (m.get(k) ?? 0) + 1)
  }
  return m
}

function sortedKeys(m: Map<string, number>): string[] {
  return Array.from(m.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([k]) => k)
}

/**
 * Compact-rendered amount (e.g. ₦4.2M, ₦750K, ₦820). Used inside
 * the prefilled summary string — the case detail's number
 * formatting is richer; this is just the seeded copy.
 */
function formatAmountMinor(minor: number): string {
  const naira = minor / 100
  if (naira >= 1_000_000) return `₦${(naira / 1_000_000).toFixed(1)}M`
  if (naira >= 1_000) return `₦${(naira / 1_000).toFixed(1)}K`
  return `₦${naira.toFixed(0)}`
}

function ConfirmStep({
  alertIds,
  mode,
  pickedCaseTitle,
  newTitle,
  pending,
  onBack,
  onConfirm,
}: {
  alertIds: readonly string[]
  mode: 'pick' | 'create'
  pickedCaseTitle: string | null
  newTitle: string
  pending: boolean
  onBack: () => void
  onConfirm: () => void
}) {
  const target =
    mode === 'pick'
      ? pickedCaseTitle ?? 'the selected case'
      : `a new case titled "${newTitle.trim()}"`
  return (
    <>
      <DialogHeader>
        <DialogTitle>Confirm grouping</DialogTitle>
        <DialogDescription>
          You're about to bundle {alertIds.length} alerts. Once linked, all case actions
          (status, notes, escalations) apply to every alert in the bundle.
        </DialogDescription>
      </DialogHeader>
      <div className="flex items-start gap-2 rounded-md border border-status-pending/30 bg-status-pending/5 px-3 py-2 text-sm">
        <AlertTriangle size={14} className="mt-0.5 shrink-0 text-status-pending" aria-hidden />
        <p className="text-fg">
          Linking {alertIds.length} alert{alertIds.length === 1 ? '' : 's'} to {target}.
          Use Undo if this was a mistake — the toast lasts 10 seconds.
        </p>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onBack}>
          Back
        </Button>
        <Button disabled={pending} onClick={onConfirm}>
          {pending ? 'Linking…' : 'Confirm'}
        </Button>
      </DialogFooter>
    </>
  )
}

function ModeTab({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        '-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors',
        active
          ? 'border-primary text-fg font-medium'
          : 'border-transparent text-muted-fg hover:text-fg',
      )}
    >
      {children}
    </button>
  )
}
